/**
 * Pivot Points (Standard / Classic)
 *
 * Calculates horizontal support and resistance levels from the prior
 * period's High, Low, and Close.
 * PP = (H + L + C) / 3
 * R1 = 2×PP − L  |  S1 = 2×PP − H
 * R2 = PP + (H − L)  |  S2 = PP − (H − L)
 * R3 = H + 2×(PP − L)  |  S3 = L − 2×(H − PP)
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const pivotPointsIndicator: IndicatorDefinition = {
  id: "pivotpoints",
  name: "Pivot Points",
  shortName: "Pivot",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "type", label: "Type", type: "select", default: "standard", options: [
      { label: "Standard", value: "standard" },
      { label: "Fibonacci", value: "fibonacci" },
    ]},
  ],
  plots: [
    { key: "pp", label: "PP", color: "#FFB300", style: "line", lineWidth: 1 },
    { key: "r1", label: "R1", color: "#EF5350", style: "line", lineWidth: 1 },
    { key: "r2", label: "R2", color: "#E53935", style: "line", lineWidth: 1 },
    { key: "r3", label: "R3", color: "#C62828", style: "line", lineWidth: 1 },
    { key: "s1", label: "S1", color: "#66BB6A", style: "line", lineWidth: 1 },
    { key: "s2", label: "S2", color: "#43A047", style: "line", lineWidth: 1 },
    { key: "s3", label: "S3", color: "#2E7D32", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { type: pivotType = "standard" } = params;
    if (candles.length < 2) {
      return [
        { key: "pp", data: [] }, { key: "r1", data: [] }, { key: "r2", data: [] }, { key: "r3", data: [] },
        { key: "s1", data: [] }, { key: "s2", data: [] }, { key: "s3", data: [] },
      ];
    }

    const ppData: { time: number; value: number }[] = [];
    const r1Data: { time: number; value: number }[] = [];
    const r2Data: { time: number; value: number }[] = [];
    const r3Data: { time: number; value: number }[] = [];
    const s1Data: { time: number; value: number }[] = [];
    const s2Data: { time: number; value: number }[] = [];
    const s3Data: { time: number; value: number }[] = [];

    // Group candles into sessions (using day boundary heuristic)
    // For intraday data, each "day" boundary produces new pivots
    // Simple approach: use running high/low/close and recalculate on day change
    let sessionHigh = candles[0].high;
    let sessionLow = candles[0].low;
    let sessionClose = candles[0].close;
    let lastPP = NaN, lastR1 = NaN, lastR2 = NaN, lastR3 = NaN;
    let lastS1 = NaN, lastS2 = NaN, lastS3 = NaN;

    // Detect day boundaries (time diff > 4 hours suggests new session for intraday)
    for (let i = 1; i < candles.length; i++) {
      const timeDiff = candles[i].time - candles[i - 1].time;
      const isNewSession = timeDiff > 14400; // 4 hours

      if (isNewSession) {
        // Calculate pivots from previous session
        const H = sessionHigh;
        const L = sessionLow;
        const C = sessionClose;
        const pp = (H + L + C) / 3;

        if (pivotType === "fibonacci") {
          const range = H - L;
          lastPP = pp;
          lastR1 = pp + 0.382 * range;
          lastR2 = pp + 0.618 * range;
          lastR3 = pp + range;
          lastS1 = pp - 0.382 * range;
          lastS2 = pp - 0.618 * range;
          lastS3 = pp - range;
        } else {
          // Standard
          lastPP = pp;
          lastR1 = 2 * pp - L;
          lastR2 = pp + (H - L);
          lastR3 = H + 2 * (pp - L);
          lastS1 = 2 * pp - H;
          lastS2 = pp - (H - L);
          lastS3 = L - 2 * (H - pp);
        }

        // Reset session tracking
        sessionHigh = candles[i].high;
        sessionLow = candles[i].low;
      } else {
        sessionHigh = Math.max(sessionHigh, candles[i].high);
        sessionLow = Math.min(sessionLow, candles[i].low);
      }
      sessionClose = candles[i].close;

      if (!isNaN(lastPP)) {
        ppData.push({ time: candles[i].time, value: lastPP });
        r1Data.push({ time: candles[i].time, value: lastR1 });
        r2Data.push({ time: candles[i].time, value: lastR2 });
        r3Data.push({ time: candles[i].time, value: lastR3 });
        s1Data.push({ time: candles[i].time, value: lastS1 });
        s2Data.push({ time: candles[i].time, value: lastS2 });
        s3Data.push({ time: candles[i].time, value: lastS3 });
      }
    }

    return [
      { key: "pp", data: ppData },
      { key: "r1", data: r1Data },
      { key: "r2", data: r2Data },
      { key: "r3", data: r3Data },
      { key: "s1", data: s1Data },
      { key: "s2", data: s2Data },
      { key: "s3", data: s3Data },
    ];
  },
};
